/* =============================================================
   SEARCH LABELS
   =============================================================
   How a search result is described, in one place, so the result
   list, the map, the berth profile and the comparison all say the
   same thing about the same berth.

   The order of the checks is the order that matters to someone
   planning a call: a ship that does not fit is out whatever the
   price; a berth that is taken is out whatever the amenities;
   missing amenities are a warning, not a refusal.
   ============================================================= */

import { formatEur } from './bookingLabels'
import { useAmenities } from './amenities'
import type { BerthResult, BookingMode } from './types'

export type ResultTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger'

export interface ResultStatus {
  label: string
  tone: ResultTone
  /** One line of why, when there is a why. */
  detail?: string
  /** Can a booking request be sent for this berth, ship and stay? */
  bookable: boolean
}

export function resultStatus(r: BerthResult, hasStay: boolean): ResultStatus {
  if (!r.fits) return { label: "Doesn't fit", tone: 'danger', detail: r.misfit ?? undefined, bookable: false }
  if (!hasStay) return { label: 'Pick dates', tone: 'neutral', detail: 'Choose arrival and departure to see price and availability.', bookable: false }
  if (r.is_free === false) return { label: 'Not available', tone: 'danger', detail: 'Booked or closed for part of this stay.', bookable: false }
  if (r.price_eur === null) return { label: 'No price', tone: 'neutral', detail: 'The owner has no rate for this ship and stay.', bookable: false }
  if (r.missing_amenities.length > 0) {
    const { amenityLabel } = useAmenities()
    return {
      label: 'Available',
      tone: 'warning',
      detail: `No ${r.missing_amenities.map(amenityLabel).join(', ').toLowerCase()}`,
      bookable: true,
    }
  }
  return { label: 'Available', tone: 'success', bookable: true }
}

/** "Instant confirmation" / "Owner confirms within 12 h". */
export function bookingModeText(mode: BookingMode, responseHours: number | null): string {
  if (mode === 'instant') return 'Instant confirmation'
  return responseHours ? `Owner confirms within ${responseHours} h` : 'Owner confirms'
}

/** "250 m" under a kilometre, "5.1 km" above. */
export function formatDistance(km: number): string {
  return km < 1 ? `${Math.round((km * 1000) / 10) * 10} m` : `${km.toFixed(1)} km`
}

/** The short price for a pin or a card: "€1,380" -- no cents, it has to fit on a map. */
export function shortPrice(eur: number | null): string | undefined {
  if (eur === null) return undefined
  return formatEur(eur).replace(/\.00$/, '')
}

/**
 * Best first: bookable ones by distance, then the rest by distance -- so the
 * answer is at the top and the near-misses, with their reasons, below it.
 */
export function rankResults(results: BerthResult[], hasStay: boolean): BerthResult[] {
  return [...results].sort((a, b) => {
    const ab = resultStatus(a, hasStay).bookable || (!hasStay && a.fits) ? 0 : 1
    const bb = resultStatus(b, hasStay).bookable || (!hasStay && b.fits) ? 0 : 1
    return ab - bb || a.distance_km - b.distance_km
  })
}
